import React from "react";
import moment from "moment-timezone";
import "./CreditSummaryTable.css";

// Session Credit Summary: what staff can book for this customer now.
//
// Rows come from GET /api/staff/customers/:id/credit-summary, which applies
// the owner's display rule on the server:
//   - memberships: active or paused, even at 0 left, with the reset date
//   - packages: credits left OR upcoming bookings ("0 left, N booked"),
//     not expired; no expiry date shows "No expiry"
//   - drop-ins, Wednesday-only, gifts, intro offer, corporate rate:
//     only with credits left and not expired
//   - "Booked" = credits held by upcoming bookings
//   - expiringSoon = expires within 14 days
// Completed counts are not shown here (Session Credits tab).

export const CREDIT_TYPE_LABELS = {
  membership: "Membership",
  package: "Package",
  drop_in: "Drop-in",
  wellness_wednesday: "Wednesday only",
  gift: "Gift",
  intro_offer: "Intro offer",
  corp_rate: "Corporate rate",
};

const SERVICE_LABELS = {
  sauna: "Sauna",
  redlight: "Red light",
};

const formatDate = (value) =>
  value ? moment.utc(value).tz("America/Phoenix").format("MMM D, YYYY") : "";

// "Resets Nov 9, 2026", "Expires Feb 28, 2027", or "No expiry".
export const resetsOrExpiresLabel = (row) => {
  if (row.category === "membership") {
    return row.resetsAt ? `Resets ${formatDate(row.resetsAt)}` : "No reset date";
  }
  if (row.expiresAt) return `Expires ${formatDate(row.expiresAt)}`;
  return "No expiry";
};

const CreditSummaryTable = ({ summary, loading = false, error = false }) => {
  if (loading && !summary) {
    return <p className="credit-table__status">Loading credits...</p>;
  }
  if (error && !summary) {
    return (
      <p className="credit-table__status credit-table__status--error">
        Could not load the credits. Refresh the page to try again.
      </p>
    );
  }

  const rows = summary?.rows || [];
  if (rows.length === 0) {
    return (
      <div className="credit-table__empty" data-testid="credit-table-empty">
        <p>No credits available.</p>
        <a href="/drop-in-sessions" target="_blank" rel="noopener noreferrer">
          See drop-in pricing
        </a>
      </div>
    );
  }

  return (
    <div className="credit-table__wrap">
      <table className="credit-table" data-testid="credit-table">
        <thead>
          <tr>
            <th scope="col">Credit source</th>
            <th scope="col">Type</th>
            <th scope="col" className="credit-table__num">
              Left
            </th>
            <th scope="col" className="credit-table__num">
              Booked
            </th>
            <th scope="col">Resets or expires</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} data-testid={`credit-row-${row.key}`}>
              <th scope="row" className="credit-table__source">
                <span className="credit-table__name">{row.name}</span>
                {SERVICE_LABELS[row.service] && (
                  <span className="credit-table__note">
                    {SERVICE_LABELS[row.service]}
                  </span>
                )}
                {row.unlimited && (
                  <span className="credit-table__note">Unlimited plan</span>
                )}
                {(row.paused || row.pauseScheduled) && (
                  <span className="credit-table__flag credit-table__flag--paused">
                    {row.paused ? "Paused" : "Pause scheduled"}
                  </span>
                )}
              </th>
              <td>
                <span className={`credit-table__tag credit-table__tag--${row.category}`}>
                  {CREDIT_TYPE_LABELS[row.category] || row.category}
                </span>
              </td>
              <td className="credit-table__num" data-label="Left">
                {row.remaining}
              </td>
              <td className="credit-table__num" data-label="Booked">
                {row.booked}
              </td>
              <td className="credit-table__date">
                {resetsOrExpiresLabel(row)}
                {row.expiringSoon && (
                  <span className="credit-table__flag credit-table__flag--warning">
                    Expires within 14 days
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CreditSummaryTable;
